<template>
  <div class="square-button-with-label" :class="classes">
    <button class="square-button ri-large-bold-body"
            @click.stop="onClick"
            :class="[variant, color, loading ? 'loading' : '']"
            :disabled="disabled || loading">
      <spinner v-if="loading" size="small"/>
      <icon v-else-if="iconProps" v-bind="iconPropsWithPredefinedValues"/>
    </button>
    <div class="button-label ri-bold-label">{{ title }}</div>
  </div>
</template>

<script>
import Colors from '../scss/riseup-js-colors.module.scss';
import { VARIANTS, RISEUP_BUTTON_VARIANT, COLORS, RISEUP_BUTTON_COLORS } from './RiseupButton/RiseupButton.consts';
import Icon from './Icon';
import Spinner from './Spinner';

export const SquareButtonProps = {
  title: {
    type: String,
    required: true,
  },
  variant: {
    type: String,
    default: RISEUP_BUTTON_VARIANT.PRIMARY,
    validator: val => VARIANTS.includes(val),
  },
  color: {
    type: String,
    default: RISEUP_BUTTON_COLORS.BLUE,
    validator: val => COLORS.includes(val),
  },
  disabled: {
    type: Boolean,
    default: false,
  },
  loading: {
    type: Boolean,
    default: false,
  },
  iconProps: {
    type: Object,
    required: false,
  },
};

export default {
  name: 'RiseupButton',
  components: {
    Icon,
    Spinner,
  },
  props: SquareButtonProps,
  data() {
    return {
      spinnerColor: Colors.riseupWhite,
    };
  },
  computed: {
    classes() {
      return {
        [this.variant]: true,
        [this.color]: true,
        disabled: this.disabled,
        loading: this.loading,
      };
    },
    iconPropsWithPredefinedValues() {
      return {
        fill: true, // we allow overriding this value, but the default is true
        ...this.iconProps,
        size: '24px',
      };
    },
  },
  methods: {
    onClick(event) {
      if (!this.disabled && !this.loading) {
        this.$emit('click');
      }
    },
  },
};
</script>

<style lang="scss" scoped>
@import '../scss/riseup-colors';
@import "../scss/border-radius";
@import "../scss/typography";
@import "../scss/spacings";
@import "./RiseupButton/riseup-button";

$square-button-size: 64px;
.square-button-with-label {
  width: $square-button-size;
  display: flex;
  flex-direction: column;
  align-items: center;

  &.disabled {
    opacity: 0.5;
  }
  &.disabled, &.loading {
    .square-button {
      cursor: default;
    }
  }
  &:not(.disabled,.loading) {
    .square-button:active {
      -webkit-filter: brightness(85%);
    }
  }

  @each $name, $colors in $button-colors {
    // $colors: [background, text]
    &.#{$name} {
      .square-button.primary {
        color: nth($colors, 2);
        background: nth($colors, 1);
        --spinner-color: #{nth($colors, 2)};
      }
      .square-button.secondary {
        background: none;
        color: nth($colors, 1);
        border: 1px solid nth($colors, 1);
        --spinner-color: #{nth($colors, 1)};
      }
      .button-label {
        color: nth($colors, 1);
      }
    }
  }

  .square-button {
    display: inline-flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    height: $square-button-size;
    width: $square-button-size;
    border-radius: $button-border-radius;
    cursor: pointer;
    border: none;
  }

  .button-label {
    margin-top: $ri-spacing-xs;
    overflow: hidden;
    display: -webkit-box;
    -webkit-line-clamp: 2; /* number of lines to show */
    line-clamp: 2;
    -webkit-box-orient: vertical;
    text-overflow: ellipsis;
    white-space: break-spaces;
    text-align: center;
  }
}
</style>
